import * as d3 from "d3";
import * as d3Zoom from "d3-zoom";
import { ANIMATION_DURATION } from "../../const.js";
import { ZoomType } from "../../types.js";

export class ZoomNavigation {
  static zoomIn<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 2, duration = ANIMATION_DURATION } = args;
    value &&
      d3
        .select(element)
        .transition()
        .duration(duration)
        .call((e) => {
          return zoom.scaleBy(e, value);
        });
  }

  static zoomOut<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 2, duration = ANIMATION_DURATION } = args;
    value &&
      d3
        .select(element)
        .transition()
        .duration(duration)
        .call((e) => {
          return zoom.scaleBy(e, 1 / value);
        });
  }

  static panLeft<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 50, duration = ANIMATION_DURATION } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.translateTo(e, -value, 0);
      });
  }

  static panRight<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 50, duration = ANIMATION_DURATION } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.translateBy(e, value, 0);
      });
  }

  static panTop<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 50, duration = ANIMATION_DURATION } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.translateTo(e, 0, -value);
      });
  }

  static panBottom<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value?: number;
    duration?: number;
  }) {
    const { element, zoom, value = 50, duration = ANIMATION_DURATION } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.translateBy(e, 0, value);
      });
  }

  static transform<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    value: d3Zoom.ZoomTransform;
    duration?: number;
  }) {
    const { element, zoom, value, duration = ANIMATION_DURATION } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.transform(e, value);
      });
  }

  static center<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    width: number;
    height: number;
    duration?: number;
  }) {
    const {
      element,
      zoom,
      width,
      height,
      duration = ANIMATION_DURATION,
    } = args;
    d3.select(element)
      .transition()
      .duration(duration)
      .call((e) => {
        return zoom.translateTo(e, 0.5 * width, 0.5 * height);
      });
  }

  static reset<T extends Element>(args: {
    element: T;
    zoom: ZoomType<T>;
    duration?: number;
  }) {
    const { element, zoom, duration = ANIMATION_DURATION } = args;
    const value = new d3Zoom.ZoomTransform(1, 0, 0);
    return ZoomNavigation.transform({
      element,
      zoom,
      value,
      duration,
    });
  }
}
